import { useEffect, useMemo, useState } from "react"
import { Box, HStack, IconButton, Skeleton, Table, Text, VStack } from "@chakra-ui/react"
import { FaTrash, FaTriangleExclamation } from "react-icons/fa6"
import SuggestionListInput from "@hydrologiq/shared/components/SuggestionListInput"
import { mappedSourceLabel, sourceDisplayLabel } from "@hydrologiq/shared/data-connections/sourceLabels"
import {
  AssetTypeLabel,
  MappedSourceText,
  MappingLockTag,
  MappingTableHeader,
} from "@hydrologiq/shared/data-connections/MappingDisplay"
import { ActionButton } from "@hydrologiq/shared/content/components/Button"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import {
  useAssetSourceMapping,
  useAssetSourceActions,
} from "../../hooks/useAssetDataConnection"

/** Cooldown after a successful reload before the button re-enables (3 min). */
const RELOAD_COOLDOWN_MS = 3 * 60 * 1000

interface AssetSourceMappingProps {
  monitoringInstanceId: string
  assetId: string
  assetClass: string | null | undefined
  /** Mappings are locked while the connection is active. */
  isActive: boolean
  onChanged: () => void
  /** Lets the parent gate activation on a mapping existing. */
  onMappingChange: (hasMapping: boolean) => void
}

/**
 * Which discovered source feeds this asset.
 *
 * An asset maps to exactly one source, so this is a single picker rather than
 * the asset-to-source table a system needs.
 *
 * TODO: auto-map when the choice is not a real choice. Where exactly one
 * compatible source exists — the common case for a single asset — the user is
 * made to pick from a list of one. The backend already has the machinery:
 * getSuggestedMappings scores source/asset pairs by class compatibility, and
 * VRM auto-mapping runs the same classification when a system connection
 * activates. Mapping automatically on creation when there is one candidate,
 * and showing this picker only when there is genuine ambiguity, would remove
 * the step entirely for most asset connections.
 */
export function AssetSourceMapping({
  monitoringInstanceId,
  assetId,
  assetClass,
  isActive,
  onChanged,
  onMappingChange,
}: AssetSourceMappingProps) {
  const {
    sources,
    stale,
    supportsRefresh,
    discoveredAt,
    connectorType,
    refreshSources,
    refreshing,
    mapping,
    loadingMapping,
    refetchMapping,
  } = useAssetSourceMapping(monitoringInstanceId, assetClass, assetId)
  const { mapSource, unmapSource, mappingBusy } = useAssetSourceActions()
  const [selected, setSelected] = useState("")

  useEffect(() => {
    onMappingChange(!!mapping)
  }, [mapping, onMappingChange])

  const items = useMemo(
    () => sources.map((s) => ({ value: s.ref, label: s.label ? sourceDisplayLabel(s, sources) : s.ref })),
    [sources],
  )

  const refresh = () => {
    refetchMapping()
    onChanged()
  }

  // ─── Reload cooldown (mirrors systems ConnectionMappingPanel) ───────────────

  const [cooldownRemaining, setCooldownRemaining] = useState(0)

  useEffect(() => {
    if (!discoveredAt) {
      setCooldownRemaining(0)
      return
    }

    const discoveredTime = new Date(discoveredAt).getTime()
    if (isNaN(discoveredTime)) {
      setCooldownRemaining(0)
      return
    }

    const tick = () => {
      const elapsed = Date.now() - discoveredTime
      const remaining = Math.max(0, RELOAD_COOLDOWN_MS - elapsed)
      setCooldownRemaining(remaining)
    }

    tick()
    const interval = setInterval(() => {
      const elapsed = Date.now() - discoveredTime
      const remaining = Math.max(0, RELOAD_COOLDOWN_MS - elapsed)
      setCooldownRemaining(remaining)
      if (remaining <= 0) clearInterval(interval)
    }, 10_000)
    return () => clearInterval(interval)
  }, [discoveredAt])

  const reloadOnCooldown = cooldownRemaining > 0

  const lastRefreshedLabel = useMemo(() => {
    if (!discoveredAt) return null
    const discoveredTime = new Date(discoveredAt).getTime()
    if (isNaN(discoveredTime)) return null
    const elapsedMs = Date.now() - discoveredTime
    if (elapsedMs < 0) return null
    const seconds = Math.floor(elapsedMs / 1000)
    if (seconds < 60) return "Last reloaded just now"
    const minutes = Math.floor(seconds / 60)
    if (minutes >= 60) return "Last reloaded > 1h ago"
    return `Last reloaded ${minutes}m ago`
  }, [discoveredAt, cooldownRemaining])

  const handleReload = async () => {
    try {
      const isStale = await refreshSources()
      if (isStale) {
        toaster.warning({
          title: "Unable to reach the data source",
          description: "Sources shown may be outdated. Check connection credentials or try again shortly.",
        })
      } else {
        toaster.success({ title: "Sources reloaded successfully" })
      }
      onChanged()
    } catch (error: any) {
      toaster.error({
        title: "Failed to refresh sources",
        description: error?.message ?? "An unexpected error occurred",
      })
    }
  }

  if (loadingMapping) return <Skeleton height="40px" width="100%" />

  return (
    <VStack align="stretch" gap={2} width="100%">
      <HStack justify="space-between" align="center">
        <HStack flexWrap="wrap">
          <Text fontWeight="semibold" fontSize="sm">Mapped assets</Text>
          <MappingLockTag isEditable={!isActive} />
        </HStack>

        {!isActive && supportsRefresh && (
          <HStack gap={2} align="center">
            {lastRefreshedLabel && (
              <Text fontSize="xs" color="fg.muted">{lastRefreshedLabel}</Text>
            )}
            <ActionButton
              size="xs"
              disabled={refreshing || reloadOnCooldown}
              context={{
                label: refreshing ? "Reloading…" : "Reload sources",
                description: "Reload available sources from the connection",
                variant: "secondary",
              }}
              onClick={handleReload}
            />
          </HStack>
        )}
      </HStack>

      {stale && (
        <HStack gap={2} align="center">
          <Box color="orange.500" fontSize="xs">
            <FaTriangleExclamation />
          </Box>
          <Text fontSize="xs" color="fg.muted">
            Could not reach the connector, so this list may be out of date.
          </Text>
        </HStack>
      )}

      {mapping ? (
        <Table.Root variant="hydrologiq" size="sm">
          <MappingTableHeader connectorType={connectorType} />
          <Table.Body>
            <Table.Row>
              <Table.Cell>{mapping.assetName}</Table.Cell>
              <Table.Cell>
                <AssetTypeLabel assetClass={mapping.assetClass} />
              </Table.Cell>
              <Table.Cell>
                <MappedSourceText
                  sourceLabel={mapping.sourceLabel || mapping.sourceRef}
                  sourceName={mapping.sourceLabel ? mappedSourceLabel(mapping.sourceRef, mapping.sourceLabel, sources) : null}
                />
              </Table.Cell>
              <Table.Cell textAlign="end">
                {!isActive && (
                  <Tooltip content="Remove this mapping" positioning={{ placement: "top" }}>
                    <Box display="inline-flex">
                      <IconButton
                        aria-label="Remove mapping"
                        variant={"hydrologiq_icon" as any}
                        size="xs"
                        disabled={mappingBusy}
                        onClick={async () => {
                          try {
                            await unmapSource(mapping.id)
                            toaster.success({ title: "Mapping removed" })
                            refresh()
                          } catch (err: any) {
                            toaster.error({ title: "Failed", description: err?.message })
                          }
                        }}
                      >
                        <FaTrash />
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table.Root>
      ) : (
        <HStack gap={3} align="center">
          <Box flex="1">
            <SuggestionListInput
              value={selected}
              onChange={setSelected}
              items={items}
              disabled={mappingBusy || sources.length === 0}
              placeholder={
                sources.length === 0
                  ? "No compatible sources on this connection"
                  : "Select a data source…"
              }
            />
          </Box>
          <ActionButton
            disabled={!selected || mappingBusy}
            context={{
              label: "Map source",
              description: "Attribute this source to the asset",
              variant: !selected || mappingBusy ? "secondary" : "primary",
            }}
            onClick={async () => {
              try {
                await mapSource(monitoringInstanceId, assetId, selected)
                toaster.success({ title: "Source mapped" })
                setSelected("")
                refresh()
              } catch (err: any) {
                toaster.error({ title: "Failed to map", description: err?.message })
              }
            }}
          />
        </HStack>
      )}
    </VStack>
  )
}

export default AssetSourceMapping
